import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import type { Plan } from "./types";
import FeatureList from "./FeatureList.tsx";

interface PlanSelectorProps {
  plans: Plan[];
  selectedPlanId: number | null;
  currentPlanId: number | null;
  billingCycle: "MONTHLY" | "YEARLY";
  onSelectPlan: (planId: number, pricing: any) => void;
  onCycleChange: (cycle: "MONTHLY" | "YEARLY") => void;
  recommendedPlanId?: number;
  billingCycles: [];
  allFeatures: [];
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  plans,
  selectedPlanId,
  currentPlanId,
  billingCycle,
  onSelectPlan,
  onCycleChange,
  recommendedPlanId = 2,
  billingCycles,
  allFeatures,
}) => {
  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);

  const getPricing = (plan: Plan) => {
    return plan.pricing.find((p) => p.displayName === billingCycle);
  };

  const tierColors: Record<
    number,
    { gradient: string; accent: string; glow: string }
  > = {
    1: {
      gradient: "linear-gradient(145deg, #f5f0eb 0%, #efe6dd 100%)",
      accent: "#8b7355",
      glow: "0 8px 32px rgba(139, 115, 85, 0.12)",
    },
    2: {
      gradient: "linear-gradient(145deg, #f0ebe8 0%, #e8ddd6 100%)",
      accent: "#371b17",
      glow: "0 8px 32px rgba(55, 27, 23, 0.18)",
    },
    3: {
      gradient: "linear-gradient(145deg, #371b17 0%, #5a3a35 100%)",
      accent: "#c9a84c",
      glow: "0 12px 40px rgba(55, 27, 23, 0.3)",
    },
  };

  const renderFeatures = (planFeatures, allFeatures, isPremium) => {
    const planFeatureIds = new Set(planFeatures?.map((f) => f.featureId));

    return allFeatures.map((feature) => {
      const isIncluded = planFeatureIds.has(feature.featureId);

      return (
        <Box
          key={feature.featureId}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            opacity: isIncluded ? 1 : 0.4,
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: isIncluded
                ? isPremium
                  ? "rgba(201, 168, 76, 0.15)"
                  : "rgba(55, 27, 23, 0.08)"
                : "rgba(0,0,0,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CheckIcon
              sx={{
                fontSize: 14,
                color: isIncluded
                  ? isPremium
                    ? "#c9a84c"
                    : "#371b17"
                  : "gray",
              }}
            />
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: isIncluded
                ? isPremium
                  ? "rgba(255,255,255,0.85)"
                  : "text.primary"
                : "gray",
            }}
          >
            {feature.displayName}
          </Typography>
        </Box>
      );
    });
  };

  return (
    <Box>
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Chip
          icon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
          label="Upgrade your restaurant"
          sx={{
            mb: 2,
            bgcolor: "rgba(55, 27, 23, 0.06)",
            color: "#371b17",
            fontWeight: 600,
            border: "1px solid rgba(55, 27, 23, 0.1)",
          }}
        />
        <Typography variant="h4" sx={{ color: "#371b17", mb: 1 }}>
          Choose the Perfect Plan
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", mb: 4, maxWidth: 500, mx: "auto" }}
        >
          Unlock powerful tools to grow your restaurant business. Switch plans
          anytime.
        </Typography>

        <ToggleButtonGroup
          value={billingCycle}
          exclusive
          onChange={(_, val) => val && onCycleChange(val)}
          sx={{
            bgcolor: "rgba(55, 27, 23, 0.04)",
            borderRadius: 3,
            p: 0.5,
            "& .MuiToggleButton-root": {
              px: 4,
              py: 1.2,
              border: "none",
              borderRadius: "10px !important",
              color: "#371b17",
              fontWeight: 600,
              transition: "all 0.3s ease",
              "&.Mui-selected": {
                bgcolor: "#371b17",
                color: "#fff !important",
                boxShadow: "0 4px 12px rgba(55, 27, 23, 0.3)",
                "&:hover": { bgcolor: "#5a3a35" },
              },
              "&:hover": { bgcolor: "rgba(55, 27, 23, 0.06)" },
            },
          }}
        >
          {billingCycles?.map((cycle) => (
            <ToggleButton
              key={cycle.cycleKey}
              value={cycle.displayName}
              className="subscription-radio-button"
            >
              {cycle.displayName}
            </ToggleButton>
          ))}
          {/* <ToggleButton value="MONTHLY">Monthly</ToggleButton>
          <ToggleButton value="YEARLY">
            Yearly
            <Chip
              label="Save up to 25%"
              size="small"
              sx={{
                ml: 1,
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: "#c9a84c",
                color: "#fff",
              }}
            />
          </ToggleButton> */}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          // display: "grid",
          // gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          // gap: 3,
          // alignItems: "stretch",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "center",
          gap: 3,
        }}
      >
        {plans.map((plan) => {
          const pricing = getPricing(plan);
          const isSelected = selectedPlanId === plan.planId;
          const isCurrent = currentPlanId === plan.planId;
          const isRecommended = plan.planId === recommendedPlanId;
          const isPremium = plan.planId === 3;
          const colors = tierColors[plan.planId] || tierColors[1];

          return (
            <Card
              key={plan.planId}
              sx={{
                cursor: isCurrent ? "default" : "pointer",
                position: "relative",
                background: colors.gradient,
                border: isSelected
                  ? "2px solid #371b17"
                  : isCurrent
                    ? "2px solid #c9a84c"
                    : "2px solid transparent",
                transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": isCurrent
                  ? {}
                  : {
                      boxShadow: colors.glow,
                      transform: "translateY(-6px)",
                    },
                ...(isRecommended && {
                  transform: { md: "scale(1.04)" },
                  zIndex: 1,
                  "&:hover": {
                    boxShadow: colors.glow,
                    transform: { md: "scale(1.04) translateY(-6px)" },
                  },
                }),
              }}
              onClick={() => !isCurrent && onSelectPlan(plan.planId, pricing)}
            >
              {/* {isRecommended && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: 14, color: "#371b17 !important" }} />}
                  label="Most Popular"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -14,
                    left: "50%",
                    transform: "translateX(-50%)",
                    bgcolor: "#c9a84c",
                    color: "#371b17",
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    boxShadow: "0 4px 12px rgba(201, 168, 76, 0.4)",
                  }}
                />
              )} */}
              {isCurrent && (
                <Chip
                  label="Current Plan"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: "#c9a84c",
                    color: "#371b17",
                    fontWeight: 700,
                    fontSize: 11,
                  }}
                />
              )}

              {isSelected && !isCurrent && (
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    color: isPremium ? "#c9a84c" : "#371b17",
                    fontSize: 28,
                  }}
                />
              )}

              <CardContent sx={{ p: 3.5, pt: isRecommended ? 4.5 : 3.5 }}>
                <Typography
                  variant="h5"
                  sx={{ color: isPremium ? "#fff" : "#371b17", mb: 0.5 }}
                >
                  {plan.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: isPremium
                      ? "rgba(255,255,255,0.7)"
                      : "text.secondary",
                    mb: 3,
                    minHeight: 40,
                    lineHeight: 1.6,
                  }}
                >
                  {plan.tagLine}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  {pricing && pricing.discountPct > 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        textDecoration: "line-through",
                        color: isPremium
                          ? "rgba(255,255,255,0.4)"
                          : "text.disabled",
                        fontSize: 13,
                      }}
                    >
                      £{formatPrice(pricing.subPrice)}
                    </Typography>
                  )}
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: isPremium ? "#fff" : "#371b17",
                        fontSize: { xs: 32, md: 36 },
                      }}
                    >
                      £{formatPrice(pricing?.discountedPrice ?? 0)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isPremium
                          ? "rgba(255,255,255,0.5)"
                          : "text.secondary",
                      }}
                    >
                      /{billingCycle == "Monthly Plan" ? "mo" : "yr"}
                    </Typography>
                  </Box>
                  {pricing && pricing.discountPct > 0 && (
                    <Chip
                      label={`Save ${pricing.discountPct}%`}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: isPremium
                          ? "rgba(201, 168, 76, 0.2)"
                          : "rgba(55, 27, 23, 0.08)",
                        color: isPremium ? "#c9a84c" : "#371b17",
                        fontWeight: 700,
                        fontSize: 11,
                        border: isPremium
                          ? "1px solid rgba(201, 168, 76, 0.3)"
                          : "none",
                      }}
                    />
                  )}
                </Box>

                {/* <Box
                  sx={{
                    bgcolor: isPremium
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(55, 27, 23, 0.04)",
                    borderRadius: 2.5,
                    px: 2,
                    py: 1.2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: isPremium ? "#c9a84c" : colors.accent,
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    {plan.commissionRate}% commission per order
                  </Typography>
                </Box> */}

                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  {/* {allFeatures.map((f) => (
                    <Box
                      key={f.featureId}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          bgcolor: isPremium
                            ? "rgba(201, 168, 76, 0.15)"
                            : "rgba(55, 27, 23, 0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CheckIcon
                          sx={{
                            fontSize: 14,
                            color: isPremium ? "#c9a84c" : "#371b17",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isPremium
                            ? "rgba(255,255,255,0.85)"
                            : "text.primary",
                        }}
                      >
                        {f.displayName}
                      </Typography>
                    </Box>
                  ))}*/}
                  <FeatureList
                    planFeatures={plan.features}
                    allFeatures={allFeatures}
                    isPremium={isPremium}
                  />
                </Stack>

                <Button
                  fullWidth
                  variant={
                    isPremium
                      ? "contained"
                      : isSelected
                        ? "contained"
                        : "outlined"
                  }
                  disabled={isCurrent}
                  sx={{
                    py: 1.5,
                    ...(isPremium && {
                      bgcolor: "#c9a84c",
                      color: "#371b17",
                      "&:hover": { bgcolor: "#b8963f" },
                    }),
                    ...(isCurrent && {
                      bgcolor: "rgba(55, 27, 23, 0.08)",
                      color: "#371b17",
                      border: "none",
                    }),
                  }}
                  onClick={(e) => {
                    // e.stopPropagation();
                    if (!isCurrent) onSelectPlan(plan.planId, pricing);
                  }}
                >
                  {isCurrent
                    ? "Current Plan"
                    : isSelected
                      ? "Selected ✓"
                      : plan.planId > (currentPlanId ?? 0)
                        ? "Upgrade"
                        : "Switch Plan"}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default PlanSelector;
